import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ConfirmDialog } from '../profile/ConfirmDialog.js';
import {
  EXPENSE_STATUS_LABEL,
  EXPENSE_STATUS_ORDER,
  formatCents,
  type BalanceDto,
  type ConversationDto,
  type ExpenseDto,
  type UserDto,
} from '@initiative/shared';
import { EmptyState, Spinner } from '../../components/Feedback.js';
import { Screen } from '../../components/Screen.js';
import { useListenfilter, type Facette } from '../../components/Listenfilter.js';
import { api } from '../../lib/api.js';
import { realtime } from '../../lib/realtime.js';
import { useChat } from '../../state/chat.js';
import { useNamen } from '../../state/leute.js';
import { useMyId } from '../../state/session.js';
import { toast } from '../../state/ui.js';
import { ExpenseSheet } from './ExpenseSheet.js';
import { SettleSheet } from './SettleSheet.js';

/**
 * „Ausgaben“ – wer hat ausgelegt, wer schuldet wem wie viel.
 *
 * Ganz oben steht die Antwort auf die einzige Frage, die wirklich jeden
 * interessiert: Schulde ich, oder schuldet man mir. Die Liste der einzelnen
 * Ausgaben kommt darunter.
 */
export function AusgabenScreen() {
  const myId = useMyId();
  const conversations = useChat((state) => state.conversations);

  const [chatId, setChatId] = useState('');
  const [expenses, setExpenses] = useState<ExpenseDto[]>([]);
  const [balances, setBalances] = useState<BalanceDto[]>([]);
  const [leute, setLeute] = useState<Record<string, UserDto>>({});
  const [laedt, setLaedt] = useState(true);
  const [neu, setNeu] = useState(false);
  const [zahlen, setZahlen] = useState<BalanceDto | null>(null);

  /**
   * Suche und Filter für die Ausgabenliste.
   *
   * Die Werte stehen bewusst nirgends aufgezählt: Kommt ein Chat dazu, eine
   * weitere Person oder später ein neuer Zustand, taucht er von selbst als
   * Filter auf. Siehe components/Listenfilter.tsx.
   */
  const laden = useCallback(async () => {
    setLaedt(true);
    try {
      const [liste, salden] = await Promise.all([
        // `includeSettled`: Eine bezahlte Ausgabe verschwand bisher aus der
        // Liste. Damit war sie auch nicht mehr nachzulesen – und wer sich
        // fragte, ob er schon gezahlt hat, fand nichts mehr vor. Sie bleibt
        // jetzt stehen und traegt ihren Zustand.
        api.expenses.list({ conversationId: chatId || undefined, includeSettled: true }),
        api.expenses.balances(chatId || undefined),
      ]);
      setExpenses(liste.items);
      setBalances(salden.items);
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Ausgaben nicht ladbar');
    } finally {
      setLaedt(false);
    }
  }, [chatId]);

  useEffect(() => {
    void laden();
  }, [laden]);

  /*
   * Echtzeit: Die Liste holt sich Änderungen selbst.
   *
   * Vorher musste man aktiv aktualisieren, um eine neue Ausgabe oder einen
   * gesetzten Haken zu sehen – und das ist bei etwas, das zu zweit gepflegt
   * wird, genau die falsche Voreinstellung. Der Server schickt jedem seine
   * eigene Fassung, weil der Zustand am Betrachter hängt.
   */
  useEffect(() => {
    const abAusgabe = realtime.on('expense.updated', ({ expense }) => {
      setExpenses((liste) => {
        const index = liste.findIndex((eintrag) => eintrag.id === expense.id);
        if (index < 0) {
          /*
           * Der Filter gilt auch für das, was hereinkommt.
           *
           * Der Server schickt das Ereignis an alle Beteiligten – unabhängig
           * davon, welcher Chat hier oben eingestellt ist. Ohne diese Prüfung
           * erschien bei gesetztem Filter „WG“ plötzlich eine Ausgabe aus der
           * Hüttengruppe ganz oben in der Liste und verschwand beim nächsten
           * vollständigen Laden wieder. Wer sie antippte, sah Zahlen, die zu
           * den Salden darüber nicht passten.
           */
          if (chatId && expense.conversationId !== chatId) return liste;
          return [expense, ...liste];
        }
        const naechste = liste.slice();
        naechste[index] = expense;
        return naechste;
      });
      // Die Salden rechnet der Server; sie lassen sich nicht aus einer
      // einzelnen Ausgabe ableiten.
      void api.expenses
        .balances(chatId || undefined)
        .then(({ items }) => setBalances(items))
        .catch(() => {});
    });
    const abGeloescht = realtime.on('expense.deleted', ({ expenseId }) => {
      setExpenses((liste) => liste.filter((eintrag) => eintrag.id !== expenseId));
      void api.expenses
        .balances(chatId || undefined)
        .then(({ items }) => setBalances(items))
        .catch(() => {});
    });
    return () => {
      abAusgabe();
      abGeloescht();
    };
  }, [chatId]);

  // Namen zu allen Kennungen, die irgendwo auftauchen – gebuendelt geholt und
  // gemerkt, siehe state/leute.ts.
  const namensIds = useMemo(() => {
    const ids = new Set<string>();
    for (const eintrag of balances) ids.add(eintrag.userId);
    for (const expense of expenses) {
      if (expense.paidBy) ids.add(expense.paidBy);
      for (const share of expense.shares) ids.add(share.userId);
    }
    return [...ids];
  }, [balances, expenses]);
  const name = useNamen(namensIds, myId);

  const facetten: Facette<ExpenseDto>[] = useMemo(
    () => [
      {
        key: 'status',
        label: 'Zustand',
        reihenfolge: EXPENSE_STATUS_ORDER,
        werte: (expense) => [{ id: expense.status, label: EXPENSE_STATUS_LABEL[expense.status] }],
      },
      {
        key: 'chat',
        label: 'Chat',
        werte: (expense) =>
          expense.conversationId
            ? [
                {
                  id: expense.conversationId,
                  label: chatName(conversations, expense.conversationId),
                },
              ]
            : [{ id: 'ohne', label: 'Nur für mich' }],
      },
      {
        key: 'ausgelegt',
        label: 'Ausgelegt von',
        werte: (expense) =>
          expense.paidBy ? [{ id: expense.paidBy, label: name(expense.paidBy) }] : [],
      },
      {
        key: 'beteiligt',
        label: 'Beteiligt',
        werte: (expense) =>
          expense.shares.map((share) => ({ id: share.userId, label: name(share.userId) })),
      },
    ],
    [conversations, name],
  );

  const filter = useListenfilter(expenses, {
    suchePlatzhalter: 'Ausgabe suchen …',
    suchtext: (expense) => `${expense.title} ${expense.note ?? ''}`,
    facetten,
  });

  const summe = useMemo(
    () => balances.reduce((wert, eintrag) => wert + eintrag.netCents, 0),
    [balances],
  );
  const waehrung = balances[0]?.currency ?? 'EUR';

  return (
    <Screen
      title="Ausgaben"
      subtitle="Wer hat ausgelegt, wer schuldet wem"
      actions={
        <button
          type="button"
          className="icon-btn"
          aria-label="Ausgabe eintragen"
          onClick={() => setNeu(true)}
        >
          ＋
        </button>
      }
    >
      <div className="field">
        <label htmlFor="exp-filter">Chat</label>
        <select
          id="exp-filter"
          className="select"
          value={chatId}
          onChange={(event) => setChatId(event.target.value)}
        >
          <option value="">Alle</option>
          {conversations.map((chat) => (
            <option key={chat.id} value={chat.id}>
              {chat.title ?? (chat.type === 'group' ? 'Gruppe' : 'Chat')}
            </option>
          ))}
        </select>
      </div>

      <section className="card stack" aria-labelledby="exp-balance-title">
        <h2 id="exp-balance-title" className="exp-title">
          Salden
        </h2>
        {laedt && balances.length === 0 ? (
          <Spinner label="Wird gerechnet …" />
        ) : balances.length === 0 ? (
          <p className="exp-hint">Alles ausgeglichen. Niemand schuldet niemandem etwas.</p>
        ) : (
          <>
            <p className={`exp-summe ${summe < 0 ? 'exp-minus' : 'exp-plus'}`}>
              {summe >= 0
                ? `Dir wird ${formatCents(summe, waehrung)} geschuldet`
                : `Du schuldest ${formatCents(-summe, waehrung)}`}
            </p>
            <ul className="list">
              {balances.map((eintrag) => (
                <li key={eintrag.userId} className="exp-balance">
                  <span className="truncate">{name(eintrag.userId)}</span>
                  <span className={eintrag.netCents < 0 ? 'exp-minus' : 'exp-plus'}>
                    {eintrag.netCents < 0
                      ? `du schuldest ${formatCents(-eintrag.netCents, eintrag.currency)}`
                      : `schuldet dir ${formatCents(eintrag.netCents, eintrag.currency)}`}
                  </span>
                  {/* Beide Richtungen brauchen einen Knopf. Bisher gab es ihn
                      nur beim Schuldner – wer Geld bekam, konnte den Eingang
                      nirgends bestaetigen, obwohl der Server es erlaubt. */}
                  <button type="button" className="btn btn-sm" onClick={() => setZahlen(eintrag)}>
                    {eintrag.netCents < 0 ? 'Zurückzahlen' : 'Erhalten?'}
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      {laedt && expenses.length === 0 ? null : expenses.length === 0 ? (
        <EmptyState
          emoji="🧾"
          title="Noch keine Ausgabe"
          description="Trag ein, was du ausgelegt hast – die App rechnet aus, wer dir wie viel schuldet."
          action={
            <button type="button" className="btn btn-primary" onClick={() => setNeu(true)}>
              Ausgabe eintragen
            </button>
          }
        />
      ) : (
        <section className="stack" aria-label="Ausgaben">
          {filter.steuerung}
          {filter.gefiltert.length === 0 ? (
            <EmptyState
              emoji="🔍"
              title="Nichts gefunden"
              description="Zu dieser Suche passt gerade keine Ausgabe."
              action={
                <button type="button" className="btn" onClick={filter.zuruecksetzen}>
                  Filter zurücksetzen
                </button>
              }
            />
          ) : null}
          {filter.gefiltert.map((expense) => (
            <ExpenseCard
              key={expense.id}
              expense={expense}
              name={name}
              myId={myId}
              onChanged={laden}
            />
          ))}
        </section>
      )}

      <ExpenseSheet
        open={neu}
        onClose={() => setNeu(false)}
        conversationId={chatId || null}
        onSaved={() => void laden()}
      />

      {zahlen && (
        <SettleSheet
          open
          onClose={() => setZahlen(null)}
          userId={zahlen.userId}
          displayName={name(zahlen.userId)}
          amountCents={zahlen.netCents}
          currency={zahlen.currency}
          onSettled={() => void laden()}
        />
      )}
    </Screen>
  );
}

function ExpenseCard({
  expense,
  name,
  myId,
  onChanged,
}: {
  expense: ExpenseDto;
  name: (id: string | null) => string;
  myId: string;
  onChanged: () => void | Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  // Löschen trifft alle im Chat und ist nicht rückgängig zu machen. Überall
  // sonst in dieser App steht davor ein zweiter Schritt.
  const [loeschFrage, setLoeschFrage] = useState(false);
  const meiner = expense.shares.find((share) => share.userId === myId);
  const offen = expense.shares.filter((share) => share.settledAt == null).length;

  async function abhaken() {
    setBusy(true);
    try {
      // Umschalten anhand des Zustands: „offen“ und „bestaetigt“ heissen beide,
      // dass ICH noch nichts gemeldet habe.
      await api.expenses.settle(expense.id, {
        settled: meiner?.status === 'open' || meiner?.status === 'confirmed',
      });
      await onChanged();
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Nicht gespeichert');
    } finally {
      setBusy(false);
    }
  }

  /** Als Auslegender den Eingang eines fremden Anteils bestaetigen. */
  async function bestaetigen(userId: string) {
    setBusy(true);
    try {
      await api.expenses.settle(expense.id, { userId, settled: true });
      await onChanged();
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Nicht gespeichert');
    } finally {
      setBusy(false);
    }
  }

  async function loeschen() {
    setBusy(true);
    try {
      await api.expenses.remove(expense.id);
      await onChanged();
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Löschen fehlgeschlagen');
      setBusy(false);
    }
  }

  return (
    <article className="card exp-card">
      <div className="row row-between">
        <strong className="truncate">{expense.title}</strong>
        <span className="exp-amount">{formatCents(expense.amountCents, expense.currency)}</span>
      </div>
      <p className="exp-meta">
        <span className={`exp-status exp-status-${expense.status}`}>
          {EXPENSE_STATUS_LABEL[expense.status]}
        </span>{' '}
        · {name(expense.paidBy)} hat ausgelegt ·{' '}
        {new Date(expense.spentAt).toLocaleDateString('de-DE')}
        {expense.hiddenFromIds.length > 0 && (
          <span className="exp-tag" title="Diese Ausgabe ist vor jemandem verborgen">
            verborgen
          </span>
        )}
      </p>
      {/* Gehoert sie zu einem Termin? Das stand bisher nirgends – man konnte
          eine Ausgabe zuordnen und sah es hinterher nicht wieder. */}
      {expense.eventId && <TerminVermerk eventId={expense.eventId} />}

      {expense.note && <p className="exp-note">{expense.note}</p>}

      <ul className="exp-shares">
        {expense.shares.map((share) => (
          <li key={share.userId} className={share.status !== 'open' ? 'exp-settled' : undefined}>
            <span className="truncate">{name(share.userId)}</span>
            <span>{formatCents(share.amountCents, expense.currency)}</span>
            {/* Der Unterschied zwischen „ich habe überwiesen“ und „ist
                angekommen“ ist genau der Punkt, an dem es sonst Streit gibt –
                also steht er dabei, nicht nur als Haken. */}
            {share.status !== 'open' && (
              <span className="exp-share-status" title={anteilHinweis(share, expense, name)}>
                {share.status === 'closed' ? '✓✓' : '✓'}{' '}
                {share.status === 'closed'
                  ? 'erledigt'
                  : share.status === 'reported'
                    ? 'gemeldet'
                    : 'bestätigt'}
              </span>
            )}
          </li>
        ))}
      </ul>

      <div className="row">
        {meiner && meiner.userId !== expense.paidBy && (
          <button
            type="button"
            className="btn btn-sm"
            disabled={busy}
            onClick={() => void abhaken()}
          >
            {meiner.status === 'reported' || meiner.status === 'closed'
              ? 'Doch noch offen'
              : 'Bezahlt'}
          </button>
        )}
        {/* Wer ausgelegt hat, bestaetigt den Eingang. Der Server liess das von
            Anfang an zu (expenses.rs: „Fremde Anteile darf nur abhaken, wer
            ausgelegt oder eingetragen hat“) – nur gab es dafuer keinen Knopf. */}
        {/*
          Der Knopf zum Bestaetigen darf NICHT verschwinden, sobald der andere
          gemeldet hat – genau dann braucht man ihn ja erst. Vorher hing er an
          `settledAt == null`, und damit war „Bezahlung erhalten“ nach einer
          Meldung nicht mehr erreichbar. Massgeblich ist, ob ICH schon
          bestaetigt habe, und das steht im Zustand des Anteils.
        */}
        {expense.paidBy === myId &&
          expense.shares
            .filter(
              (share) =>
                share.userId !== myId && (share.status === 'open' || share.status === 'reported'),
            )
            .map((share) => (
              <button
                key={share.userId}
                type="button"
                className={share.status === 'reported' ? 'btn btn-sm btn-primary' : 'btn btn-sm'}
                disabled={busy}
                onClick={() => void bestaetigen(share.userId)}
              >
                {share.status === 'reported'
                  ? `${name(share.userId)} meldet bezahlt – bestätigen`
                  : `${name(share.userId)} hat gezahlt`}
              </button>
            ))}
        {expense.canEdit && (
          <button
            type="button"
            className="btn btn-sm btn-danger"
            disabled={busy}
            onClick={() => setLoeschFrage(true)}
            data-tipp="Diese Ausgabe entfernen – bei allen, die sie sehen"
          >
            Löschen
          </button>
        )}
      </div>
      <ConfirmDialog
        open={loeschFrage}
        title={`„${expense.title}" löschen?`}
        description="Die Ausgabe verschwindet bei allen, die sie sehen, samt der Aufteilung und den bereits gesetzten Haken. Rückgängig geht das nicht."
        confirmLabel="Löschen"
        danger
        busy={busy}
        onCancel={() => setLoeschFrage(false)}
        onConfirm={() => void loeschen()}
      />
    </article>
  );
}

/** Der Name eines Chats, wie er im Filter stehen soll. */
function chatName(conversations: ConversationDto[], id: string): string {
  const chat = conversations.find((eintrag) => eintrag.id === id);
  if (!chat) return 'Chat';
  return chat.title ?? (chat.type === 'group' ? 'Gruppe' : 'Direktchat');
}

/** Wer sich zu einem Anteil geäußert hat – im Klartext für den Hinweis. */
function anteilHinweis(
  share: ExpenseDto['shares'][number],
  expense: ExpenseDto,
  name: (id: string | null) => string,
): string {
  switch (share.status) {
    case 'closed':
      return `${name(share.userId)} hat bezahlt gemeldet, ${name(expense.paidBy)} hat den Eingang bestätigt`;
    case 'reported':
      return `${name(share.userId)} meldet, bezahlt zu haben – noch nicht bestätigt`;
    case 'confirmed':
      return `${name(expense.paidBy)} hat den Eingang bestätigt`;
    default:
      return 'Noch offen';
  }
}

/**
 * „Gehört zu: Hüttenwochenende“ – mit Weg dorthin.
 *
 * Der Titel wird nachgeladen und gemerkt, statt ihn in jede Ausgabe zu
 * schreiben: Ein Termin kann umbenannt werden, und eine mitgeschriebene Kopie
 * wäre danach falsch.
 */
function TerminVermerk({ eventId }: { eventId: string }) {
  const [titel, setTitel] = useState<string | null>(null);

  useEffect(() => {
    let abgebrochen = false;
    void api.calendar
      .byId(eventId)
      .then((termin) => {
        if (!abgebrochen) setTitel(termin.title);
      })
      .catch(() => {
        // Der Termin ist fort oder nicht für mich sichtbar. Dann eben ohne
        // Namen – der Vermerk selbst stimmt ja trotzdem.
        if (!abgebrochen) setTitel('');
      });
    return () => {
      abgebrochen = true;
    };
  }, [eventId]);

  if (titel === null) return null;
  return (
    <p className="exp-event">
      <span aria-hidden="true">📅</span>{' '}
      {titel ? (
        /*
         * Zwei Fehler in einer Zeile, beide unsichtbar bis zum Antippen:
         * Das Ziel hiess `/kalender/:id`, angemeldet ist aber
         * `/kalender/termin/:id` – der Verweis lief also ins Leere. Und ein
         * blankes <a> laedt in einer PWA die ganze Seite neu, statt innerhalb
         * der App zu wechseln.
         */
        <Link to={`/kalender/termin/${eventId}`}>Gehört zu „{titel}“</Link>
      ) : (
        'Gehört zu einem Termin'
      )}
    </p>
  );
}
